import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

/**
 * With Okta handling authentication, the Redux auth slice just caches the
 * local user row returned by `GET /api/me`. That row has the Sequelize id,
 * username, email, and `role` — which the rest of the app (admin gating,
 * ownership checks) reads as `user.id` / `user.role`.
 */
export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (_args, { rejectWithValue }) => {
    try {
      const response = await api.get('/me');
      return response.data.user;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch profile'
      );
    }
  }
);

const initialState = {
  user: null,
  loading: false,
  error: null,
  /** After one completed /api/me fetch (success with or without a row, or failure). Prevents refetch loops when the API returns 200 + user: null. */
  hasLoadedMe: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearCurrentUser: (state) => {
      state.user = null;
      state.error = null;
      state.loading = false;
      state.hasLoadedMe = false;
    },
    clearError: (state) => {
      state.error = null;
      state.hasLoadedMe = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.hasLoadedMe = true;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.hasLoadedMe = true;
      });
  },
});

export const { clearCurrentUser, clearError } = authSlice.actions;
export default authSlice.reducer;
